import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button, Icon, ProgressBarBox, Typography } from '@/components/common';
import Icons from '@/const/icons';
import useModal from '@/hooks/useModal';
import { X } from 'lucide-react';
import { UploadFile } from '@/interfaces';
import { actionRequiredService } from '@/api';
import { useNotification } from '@/hooks';
import { formatErrorMessage } from '@/lib';
import { UploadContainer } from '@/components/common/upload';
import { useRouter } from 'next/router';
import clsx from 'clsx';
import { ModalHeader } from '@/components/modals/ModalHeader';
import { logApiError } from '@/lib/logApiError';

interface Props {
	applicationId: number;
	actionRequiredId: number;
	isOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	refetch: () => void;
}

export const ModalUpload: React.FC<Props> = ({
	applicationId,
	actionRequiredId,
	isOpen,
	setIsModalOpen,
	refetch,
}) => {
	const router = useRouter();
	const { pathname, query } = router;
	const { Modal, show, hide } = useModal();
	const {
		Modal: ModalDocumentUploaded,
		show: showDocumentUploaded,
		hide: hideDocumentUploaded,
	} = useModal();
	const { toastError } = useNotification();
	const [progress, setProgress] = useState(0);
	const [file, setFile] = useState<File | null>(null);
	const [error, setError] = useState<string>();
	const [isSubmitting, setIsSubmitting] = useState(false);
	// const [fileId, setFileId] = useState<number | null>(null);

	const validFileTypes = [
		'image/jpeg',
		'image/jpg',
		'image/png',
		'application/pdf',
	];

	useEffect(() => {
		if (isOpen) {
			show();
		}
	}, [isOpen]);

	const handleDropFile = (e: React.DragEvent<HTMLDivElement>): void => {
		e.preventDefault();
		e.stopPropagation();
		const droppedFile = e.dataTransfer.files[0];
		setProgress(0);
		handleUploadFile(droppedFile);
	};

	const deleteQuery = (): void => {
		delete query['type'];
		router.replace({
			pathname,
			query,
		});
	};

	const handleCloseModal = async (): Promise<void> => {
		// reset states
		hide();
		refetch();
		deleteQuery();
		setIsModalOpen(false);
		setProgress(0);
		setFile(null);
		setError(undefined);
		// setFileId(null);
	};

	const simulateProgress = async (max: number): Promise<void> => {
		return new Promise((resolve) => {
			let currentProgress = 0;
			const interval = setInterval(() => {
				currentProgress += 10;
				setProgress(currentProgress);
				if (currentProgress >= max) {
					clearInterval(interval);
					resolve();
				}
			}, 300);
		});
	};

	const handleUploadFile = async (file: File): Promise<void> => {
		setProgress(0); // Reset progress
		setError(undefined); // reset error message

		await simulateProgress(80); // Only go up to 80% until upload completes

		if (!validateImageSize(file)) return; // validate file size

		if (!isValidFileTypes(file)) return; // validate file type

		setProgress(100);
		setFile(file);
	};

	const handleSaveFile = async (file: File): Promise<void> => {
		if (file && applicationId) {
			try {
				setIsSubmitting(true);
				const data: UploadFile = {
					applicationId: applicationId,
					doc: file,
					fileName: file.name,
					actionRequiredId: actionRequiredId,
				};

				// Upload file
				await actionRequiredService.postUploadFile(data);
				showDocumentUploaded();
				hide();
			} catch (error) {
				toastError(formatErrorMessage((error as any).message));
			} finally {
				setIsSubmitting(false);
			}
		}
	};

	const handleRemoveFile = () => {
		setFile(null);
		setProgress(0);
	};

	// Logic updated by ticket 2030
	// const handleRemoveFile = async (): Promise<void> => {
	// 	try {
	// 		if (fileId) {
	// 			await actionRequiredService.deleteFile(actionRequiredId, fileId);
	// 			toastSuccess('File deleted successfully');
	// 			refetch?.();
	// 		}
	// 	} catch (error) {
	// 		toastError(formatErrorMessage((error as any).message));
	// 	} finally {
	// 		hide();
	// 		setIsModalOpen(false);
	// 		setProgress(0);
	// 		setFile(null);
	// 		setFileId(null);
	// 	}
	// };

	const validateImageSize = (file: File): boolean => {
		if (file.size > 25 * 1000 * 1024) {
			setProgress(0);
			setError(
				'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.'
			);
			logApiError({
				status: 400,
				message:
					'The file you tried to upload is too heavy. Please try again with a file up to 25 MB.',
				config: { data: null, url: router.asPath },
			});
			return false;
		}

		return true;
	};

	const isValidFileTypes = (file: File): boolean => {
		if (!validFileTypes.includes(file.type)) {
			setProgress(0);
			setError(
				'Select a file in the correct format such as pdf, png, jpg or jpeg'
			);
			logApiError({
				status: 400,
				message:
					'Select a file in the correct format such as pdf, png, jpg or jpeg',
				config: { data: null, url: router.asPath },
			});
			return false;
		}
		return true;
	};

	const getFileName = (): string => {
		if (progress == 100) {
			if (file && typeof file !== 'string' && 'name' in file) return file.name;
		}

		return '';
	};

	return (
		<>
			<Modal
				avoidClose={progress > 0 && progress < 100}
				classNameCardContainer="!bg-gradient-to-r from-white to-white"
				onClose={handleCloseModal}
			>
				<div className="w-full flex flex-col items-center space-y-6">
					<div id="header" className="space-y-3 w-full relative">
						<div className="flex items-center lg:justify-center">
							<Icon src={Icons.imageCardsAlt} className="!w-16" />
							<Typography type="semibold-20">Upload documentation</Typography>
							<button
								className="absolute -right-2 lg:right-0 lg:top-2"
								onClick={handleCloseModal}
							>
								<X />
							</button>
						</div>
					</div>
					<div
						id="content"
						className={clsx(
							'md:px-10 border-1 border-opacity-80 rounded-[32px] border border-dashed border-opacity-primary bg-gray-20',
							{
								'border-opacity-primary': !error,
								'border-red-600': error,
							}
						)}
						onDrop={handleDropFile}
						onDragOver={(e) => e.preventDefault()}
					>
						<div className="py-6 px-8 flex flex-col items-center gap-8">
							<div>
								<Typography
									type="regular-14"
									className="opacity-60 text-center"
								>
									Drag and drop your file here, or click to
								</Typography>
								<Typography
									type="regular-14"
									className="opacity-60 text-center"
								>
									browse from your device
								</Typography>
							</div>
							{progress > 0 && (
								<div className="w-48">
									<ProgressBarBox
										percent={progress}
										handleRemove={handleRemoveFile}
										fileName={getFileName()}
										isShowPrevFile={false}
									/>
								</div>
							)}
							{progress === 0 && !file && (
								<UploadContainer
									id="action-required"
									setFile={handleUploadFile}
									accept={validFileTypes.join(',')}
								>
									<div className="flex cursor-pointer items-center bg-primary rounded-full text-base font-semibold text-white gap-2 px-6 h-[30px] py-4">
										Upload
										<Icon src={Icons.documentUpload} className="!w-4 !h-4" />
									</div>
								</UploadContainer>
							)}
							{error && (
								<Typography
									type="regular-12"
									text={error}
									className="text-main-red max-w-60 mx-auto"
								/>
							)}
						</div>
					</div>
					{file && (
						<div className="flex flex-col lg:flex-row items-center gap-2">
							<Button
								label="Cancel"
								size="fit"
								decoration="secondary"
								onClick={hide}
								className="h-12"
							/>
							<Button
								label="Submit"
								size="fit"
								decoration="primary"
								onClick={() => handleSaveFile(file as File)}
								className="h-12"
								disabled={isSubmitting}
							/>
						</div>
					)}
				</div>
			</Modal>

			{/* Confirm Modal */}
			<ModalDocumentUploaded
				onClose={handleCloseModal}
				withXButton
				title={
					<ModalHeader
						title="Document uploaded"
						icon={Icons.documentsEmptyState2}
						classNameImage="w-[61px] h-[48px]"
					/>
				}
				classNameCardContainer="max-w-[532px] !px-10"
				backgroundColor="white"
			>
				<Typography type="semibold-16" className="text-black">
					Your document has been successfully submitted
				</Typography>
				<Typography type="regular-14" className="text-opacity-black mt-2.5">
					We’ll review it shortly and let you know the status by email.
				</Typography>
				<div className="flex justify-center items-center mt-8">
					<Button
						label="Close"
						size="fit"
						decoration="secondary"
						onClick={() => {
							handleCloseModal();
							hideDocumentUploaded();
						}}
						className="h-12"
					/>
				</div>
			</ModalDocumentUploaded>
		</>
	);
};
